import * as base from './base.ts'
import * as queryParser from './queryParser.ts'
import type * as cold from '@owl-nest/cold-data/client'

export const FILTERS = [
  { name: 'criterion', attribute: 'tags' },
  { name: 'category', attribute: 'tags' },
  { name: 'person', attribute: 'tags' },
] as const

export type Filters = {
  criterion: string
  category: string
  person: string
}

// Types used to ensure that names in `FILTERS` matches the complete list of keys of `Filters`
type IsValid<TYPE extends true> = TYPE
type Check = IsValid<
  keyof Filters extends (typeof FILTERS)[number]['name']
    ? (typeof FILTERS)[number]['name'] extends keyof Filters
      ? true
      : false
    : false
>

export type Query = base.Query<Filters>
export type Result = base.Result<Filters>

export function toUrlSearchParams(query: Query, _cold: typeof cold): URLSearchParams {
  const params = base.toUrlSearchParams(query)

  if (query.facetFilters?.category) {
    params.set('category', query.facetFilters.category)
  }
  if (query.facetFilters?.criterion) {
    // we use two different parameters (`environmentalPillar` and
    // `socialPillar`) instead of one `criteria` to keep the same url as before
    if (_cold.taxonomies['pilier-environnemental'].children.tags.includes(query.facetFilters?.criterion)) {
      params.set('environmentalPillar', query.facetFilters?.criterion)
    }
    if (_cold.taxonomies['pilier-social'].children.tags.includes(query.facetFilters?.criterion)) {
      params.set('socialPillar', query.facetFilters?.criterion)
    }
  }
  if (query.facetFilters?.person) {
    // we use two different parameters (`gender` and `age`) instead of one
    // `person` to keep the same url as before
    if (['femme', 'homme'].includes(query.facetFilters?.person)) {
      params.set('gender', query.facetFilters?.person)
    } else if (['enfant', 'puericulture', 'all'].includes(query.facetFilters?.person)) {
      params.set('age', query.facetFilters?.person)
    } else {
      params.set('target', query.facetFilters?.person)
    }
  }

  params.sort()

  return params
}

export function fromParsedUrlQuery(query: Record<string, string | string[] | undefined>): Query {
  return {
    ...base.fromParsedUrlQuery(query),
    facetFilters: {
      category: queryParser.string(query.category),
      person: queryParser.string(query.age) ?? queryParser.string(query.target) ?? queryParser.string(query.gender),
      criterion: queryParser.string(query.environmentalPillar) ?? queryParser.string(query.socialPillar),
    },
  }
}

export function setPerson(query: Query, person: string | undefined): Query {
  if (person === 'puericulture' || person === 'animaux') {
    return { ...query, facetFilters: { ...query.facetFilters, person, category: undefined }, page: undefined }
  }
  return { ...query, facetFilters: { ...query.facetFilters, person }, page: undefined }
}

export function setCriterion(query: Query, criterion: string | undefined): Query {
  return { ...query, facetFilters: { ...query.facetFilters, criterion: criterion }, page: undefined }
}

export function setCategory(query: Query, category: string | undefined): Query {
  return { ...query, facetFilters: { ...query.facetFilters, category }, page: undefined }
}

export function resetFilters(query: Query): Query {
  return {
    ...query,
    facetFilters: undefined,
    priceRange: undefined,
    page: undefined,
  }
}
